import {useEffect, useRef, useState} from 'react'

const PLATFORM_SRC = 'https://elfsightcdn.com/platform.js'
const APP_CLASS = 'elfsight-app-a489c969-e2dc-42dc-a1ad-8a45aa358994'

// Module-level singletons: the widget div is created ONCE and survives route
// changes. Elfsight initializes a div a single time — letting React destroy it
// on unmount forces a full widget reload (and the skeleton) every time the
// user navigates back to /help. Instead the node is adopted into the page on
// mount and parked in a hidden holder on unmount.
let widgetEl = null
let parkingLot = null
let widgetLoaded = false
let brandingObserverAttached = false

// The branding link's inline !important styles defeat CSS hiding and its
// locked margins leave an empty band — take it out of the DOM instead, and
// keep watching in case the platform re-inserts it.
function stripBranding(root) {
    root.querySelectorAll('a[href*="elfsight.com"]').forEach(a => a.remove())
}

function acquireWidgetEl() {
    if (!widgetEl) {
        widgetEl = document.createElement('div')
        widgetEl.className = APP_CLASS
    }
    return widgetEl
}

function park(el) {
    if (!parkingLot) {
        parkingLot = document.createElement('div')
        parkingLot.style.display = 'none'
        document.body.appendChild(parkingLot)
    }
    parkingLot.appendChild(el)
}

const S = ({w = '100%', h = 16, r = 8, mb = 0, className = ''}) => (
    <div className={`animate-pulse bg-gray-200 shrink-0 ${className}`}
         style={{width: w, height: h, borderRadius: r, marginBottom: mb}}/>
)

// Mirrors the old ChatSupport card layout (header avatar + name/date, message
// bubble, "Start Chat with:" icon row) so the sidebar keeps its shape while
// the external widget loads for the first time.
const ChatSkeleton = () => (
    <div className="white-global-section bg-(--bg-grey)">
        <div className="flex items-center gap-2 mb-6">
            <S w={60} h={60} r={9999}/>
            <div className="flex-1">
                <S w="40%" h={18} mb={8}/>
                <S w="55%" h={12}/>
            </div>
        </div>

        <div className="mb-8">
            <S w={40} h={10} className="ml-auto mb-2"/>
            <div className="flex items-start gap-4 lg:mb-50">
                <S w={40} h={40} r={9999} className="mt-1"/>
                <div className="flex-1 bg-white rounded-2xl border border-[#cfcdcd] px-5 py-4">
                    <S w="90%" h={12} mb={8}/>
                    <S w="60%" h={12}/>
                </div>
            </div>
        </div>

        <div className="text-center mt-8">
            <S w={120} h={16} className="mx-auto mb-4"/>
            <div className="flex items-center justify-center gap-2">
                {[0, 1, 2, 3, 4].map(i => <S key={i} w={58} h={58} r={16}/>)}
            </div>
        </div>
    </div>
)

// External Elfsight chat widget (site: https://a489c969e2dc42dca1ad8a45aa358994.elf.site).
// platform.js is injected on first mount; the widget div itself persists across
// SPA navigation (see park/acquire above), so the skeleton only ever shows on
// the very first load after a page reload.
export default function ElfsightChat() {
    const hostRef = useRef(null)
    const [loaded, setLoaded] = useState(() => widgetLoaded)

    useEffect(() => {
        const el = acquireWidgetEl()
        hostRef.current.appendChild(el)

        if (!brandingObserverAttached) {
            brandingObserverAttached = true
            stripBranding(el)
            new MutationObserver(() => stripBranding(el))
                .observe(el, {childList: true, subtree: true})
        }

        if (!document.querySelector(`script[src="${PLATFORM_SRC}"]`)) {
            const script = document.createElement('script')
            script.src = PLATFORM_SRC
            script.async = true
            document.body.appendChild(script)
        } else if (!el.childElementCount) {
            // script already on the page but this div was never initialized
            window.eapps?.Platform?.initWidgetsFromBuffer?.()
        }

        // First load only: swap the skeleton out once real content with real
        // height shows up (the div stays empty until the platform injects it).
        let mo, ro
        if (!widgetLoaded) {
            const check = () => {
                if (el.offsetHeight > 80) {
                    widgetLoaded = true
                    setLoaded(true)
                    mo?.disconnect()
                    ro?.disconnect()
                    return true
                }
                return false
            }
            if (!check()) {
                mo = new MutationObserver(check)
                mo.observe(el, {childList: true, subtree: true})
                ro = new ResizeObserver(check)
                ro.observe(el)
            }
        }

        return () => {
            mo?.disconnect()
            ro?.disconnect()
            park(el)
        }
    }, [])

    return (
        <>
            {!loaded && <ChatSkeleton/>}
            <div ref={hostRef}></div>
        </>
    )
}
